import { register } from 'swiper/element/bundle'

// Register Swiper custom elements
register()

class AnimatedMenu extends HTMLElement {
  constructor() {
    super()
    this.swiper = this.querySelector('swiper-container')
    this.menuItems = this.querySelectorAll('.menu-pagination-item')
    this.lottieContainers = this.querySelectorAll('.lottie-image-container')
    this.overflowBg = this.closest('section').querySelector('[data-bg-color]')
    this.isHero = this.dataset.isHero
    this.init()
  }

  init() {
    // Fade in the component
    requestAnimationFrame(() => this.classList.remove('opacity-0'))

    if (!this.swiper) return

    this.setupSwiper()
    this.setupMenuItems()
  }

  setupSwiper() {
    const swiperParams = {
      slidesPerView: 1,
      spaceBetween: 30,
      /* autoplay: {
        delay: 2000,
        disableOnInteraction: false,
      }, */
      on: {
        slideChange: () => {
          const activeIndex = this.swiper.swiper.activeIndex
          this.updateActiveMenuItem(activeIndex)
          this.updateLottieContainer(activeIndex)
          this.updateBackgroundColor(activeIndex)
          this.updateContentColors(activeIndex)
          if (this.isHero) {
            this.updateHeaderColors(activeIndex)
          }
        },
      },
    }

    Object.assign(this.swiper, swiperParams)
    this.swiper.initialize()
  }

  setupMenuItems() {
    this.menuItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.swiper.swiper.slideTo(index)
        this.updateActiveMenuItem(index)
        this.updateLottieContainer(index)
        this.updateBackgroundColor(index)
        this.updateContentColors(index)
      })
    })
  }

  updateActiveMenuItem(index) {
    this.menuItems.forEach((item, i) => {
      if (i === index) {
        item.classList.add('active')

        // Check if we're in mobile view (overflow-x-auto is active)
        const menuContainer = item.closest('.overflow-x-auto')
        if (menuContainer) {
          // Calculate the scroll position to center the active item
          const containerWidth = menuContainer.clientWidth
          const itemLeft = item.offsetLeft
          const itemWidth = item.clientWidth
          const scrollLeft = itemLeft - containerWidth / 2 + itemWidth / 2

          // Smoothly scroll to center the active item
          menuContainer.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          })
        }
      } else {
        item.classList.remove('active')
      }
    })
  }

  updateContentColors(index) {
    const activeMenuItem = this.menuItems[index]
    const section = this.closest('section')

    // Get the new color classes from the active menu item
    const newTextClass = activeMenuItem.dataset.textClass
    const newFillClass = activeMenuItem.dataset.fillClass

    if (newTextClass && newFillClass) {
      // Update text colors for content
      const textElements = section.querySelectorAll('h3, p, .btn-secondary')
      textElements.forEach(element => {
        // Remove all category text classes
        element.classList.remove(
          ...Array.from(element.classList).filter(
            className => className.startsWith('category-') && className.endsWith('-text')
          )
        )
        // Add the new text class
        element.classList.add(newTextClass)
      })

      // Update fill colors for icons
      const iconElements = section.querySelectorAll('.header-arrow-right')
      iconElements.forEach(element => {
        // Remove all category fill classes
        element.classList.remove(
          ...Array.from(element.classList).filter(
            className => className.startsWith('category-') && className.endsWith('-fill')
          )
        )
        // Add the new fill class
        element.classList.add(newFillClass)
      })

      // Update menu item text colors
      this.menuItems.forEach((item, i) => {
        // Remove all category text classes and opacity classes
        item.classList.remove(
          ...Array.from(item.classList).filter(
            className =>
              (className.startsWith('category-') && className.endsWith('-text')) ||
              className === 'opacity-30'
          )
        )
        // Add the new text class to all items
        item.classList.add(newTextClass)
        // Add opacity to inactive items
        if (i !== index) {
          item.classList.add('opacity-30')
        }
      })
    }
  }

  updateLottieContainer(index) {
    this.lottieContainers.forEach((container, i) => {
      if (i === index) {
        container.classList.add('active')
        container.style.opacity = '1'
        container.style.transform = 'translateX(0)'
      } else {
        container.classList.remove('active')
        container.style.opacity = '0'
        container.style.transform = 'translateX(20px)'
      }
    })
  }

  updateBackgroundColor(index) {
    const activeMenuItem = this.menuItems[index]
    const section = this.closest('section')
    const overflowBg = section.querySelector('[data-bg-color]')

    // Get the new background class from the active menu item
    const newBgClass = activeMenuItem.dataset.bgClass

    if (newBgClass) {
      // Remove all category background classes
      section.classList.remove(
        ...Array.from(section.classList).filter(
          className => className.startsWith('category-') && className.endsWith('-bg')
        )
      )
      overflowBg.classList.remove(
        ...Array.from(overflowBg.classList).filter(
          className => className.startsWith('category-') && className.endsWith('-bg')
        )
      )

      // Add the new background class
      section.classList.add(newBgClass)
      overflowBg.classList.add(newBgClass)
    }
  }

  updateHeaderColors(index) {
    const activeMenuItem = this.menuItems[index]
    const header = document.querySelector('header:not(.sticky-header)')
    const dropdownContent = document.querySelectorAll('.dropdown-content')
    const mobileMenu = document.querySelector('#mobile-menu')
    if (!header) return

    // Get the new color classes from the active menu item
    const newTextClass = activeMenuItem.dataset.textClass
    const newFillClass = activeMenuItem.dataset.fillClass
      .replace(' path', '')
      .replace(' polygon', '')
    const newStrokeClass = activeMenuItem.dataset.strokeClass
      .replace(' path', '')
      .replace(' polygon', '')
    const newBgClass = activeMenuItem.dataset.bgClass
    const inverseTextClass = activeMenuItem.dataset.inverseTextClass
    const inverseFillClass = activeMenuItem.dataset.inverseFillClass
      .replace(' path', '')
      .replace(' polygon', '')
    const btnBgClass = activeMenuItem.dataset.btnBgClass

    if (newTextClass && newFillClass && newBgClass) {
      // Update header text colors, excluding button text
      const textElements = header.querySelectorAll(
        'a:not(.dropdown-content a, .btn-sm a, .btn-lg a, a:has(.btn-sm), a:has(.btn-lg), #mobile-menu-trigger a, #mobile-menu a), p:not(.dropdown-content p, .btn-sm p, .btn-lg p, #mobile-menu-trigger p, #mobile-menu p), span:not(.dropdown-content span, .dropdown-icon, .btn-sm span, .btn-lg span, #mobile-menu-trigger span, #mobile-menu span)'
      )
      textElements.forEach(element => {
        // Skip if element is inside a button
        if (element.closest('.btn-sm, .btn-lg')) return

        // Remove all category text classes
        element.classList.remove(
          ...Array.from(element.classList).filter(
            className => className.startsWith('category-') && className.endsWith('-text')
          )
        )
        // Add the new text class
        element.classList.add(newTextClass)
      })

      // Update header icons and SVGs, excluding button SVGs
      const svgElements = header.querySelectorAll(
        'svg:not(.btn-sm svg, .btn-lg svg, #mobile-menu-trigger svg, #mobile-menu svg)'
      )
      svgElements.forEach(svg => {
        // Skip if element is inside a button
        if (svg.closest('.btn-sm, .btn-lg, #mobile-menu-trigger, #mobile-menu')) return

        // Remove all category fill classes from SVG
        svg.classList.remove(
          ...Array.from(svg.classList).filter(
            className =>
              className.startsWith('category-') &&
              (className.endsWith('-fill') ||
                className.endsWith('-fill-inverse') ||
                className.endsWith('-stroke'))
          )
        )
        // Add the new fill class to SVG
        svg.classList.add(newFillClass)
        svg.classList.add(newStrokeClass)
        // Update all SVG elements that need color changes
        const elementsToUpdate = svg.querySelectorAll('path, polygon, rect, circle, line, polyline')
        elementsToUpdate.forEach(element => {
          // Remove all category fill classes
          const classesToRemove = Array.from(element.classList).filter(
            className =>
              className.startsWith('category-') &&
              (className.endsWith('-fill') || className.endsWith('-stroke'))
          )
          classesToRemove.forEach(className => element.classList.remove(className))

          // Add the new fill class
          element.classList.add(newFillClass)
          element.classList.add(newStrokeClass)
        })
      })

      // Update mobile menu trigger SVG
      const mobileMenuTrigger = header.querySelector('#mobile-menu-trigger svg')
      if (mobileMenuTrigger) {
        // Remove all category fill classes from SVG
        mobileMenuTrigger.classList.remove(
          ...Array.from(mobileMenuTrigger.classList).filter(
            className =>
              className.startsWith('category-') &&
              (className.endsWith('-fill') ||
                className.endsWith('-fill-inverse') ||
                className.endsWith('-stroke'))
          )
        )
        // Add the new fill class to SVG
        mobileMenuTrigger.classList.add(newFillClass)
        mobileMenuTrigger.classList.add(newStrokeClass)
        // Update all SVG elements that need color changes
        const triggerElementsToUpdate = mobileMenuTrigger.querySelectorAll(
          'path, polygon, rect, circle, line, polyline'
        )
        triggerElementsToUpdate.forEach(element => {
          // Remove all category fill classes
          const classesToRemove = Array.from(element.classList).filter(
            className =>
              className.startsWith('category-') &&
              (className.endsWith('-fill') || className.endsWith('-stroke'))
          )
          classesToRemove.forEach(className => element.classList.remove(className))

          // Add the new fill class
          element.classList.add(newFillClass)
          element.classList.add(newStrokeClass)
        })
      }

      // Dropdown content colors are preserved from CSS - no changes needed

      // Mobile menu colors are preserved from CSS - no changes needed

      // Update button states
      const buttons = header.querySelectorAll(
        '.btn-sm:not([type="submit"]), .btn-lg:not([type="submit"])'
      )
      buttons.forEach(button => {
        // Remove all category classes from CTA button
        button.classList.remove(
          ...Array.from(button.classList).filter(
            className =>
              (className.startsWith('category-') &&
                (className.endsWith('-text') || className.endsWith('-border'))) ||
              className.endsWith('-inverse-text') ||
              className.endsWith('-fill') ||
              className.endsWith('-fill-inverse') ||
              className.endsWith('-bg') ||
              className.endsWith('-btn-bg') ||
              className.endsWith('-btn-bg-inverse') ||
              className === 'text-black' ||
              className === 'text-white' ||
              className === 'border-secondary' ||
              className === 'text-secondary' ||
              className === 'text-primary' ||
              className === 'border-primary' ||
              className.includes('hover:text-') ||
              className.includes('hover:fill-') ||
              className.includes('hover:stroke-')
          )
        )

        // Add the new text and border classes
        const isMobile = window.innerWidth <= 768
        if (isMobile) {
          button.classList.add(newTextClass)
          button.classList.add(newTextClass.replace('-text', '-border-text'))
        } else {
          button.classList.add(newTextClass)
          button.classList.add(newTextClass.replace('-text', '-hover-bg'))
          button.classList.add(newTextClass.replace('-text', '-hover-text'))
          button.classList.add(newTextClass.replace('-text', '-hover-fill'))
        }

        // Update button text elements
        const buttonTextElements = button.querySelectorAll('p, span')
        buttonTextElements.forEach(element => {
          element.classList.remove(
            ...Array.from(element.classList).filter(
              className =>
                (className.startsWith('category-') && className.endsWith('-text')) ||
                className === 'text-black' ||
                className === 'text-white'
            )
          )
          if (isMobile) {
            element.classList.add(newTextClass)
          } else {
            element.classList.add(inverseTextClass)
          }
        })

        // Update SVG fill within button
        const buttonSvgs = button.querySelectorAll('svg')
        buttonSvgs.forEach(svg => {
          // Remove all category fill classes
          svg.classList.remove(
            ...Array.from(svg.classList).filter(
              className =>
                (className.startsWith('category-') && className.endsWith('-fill')) ||
                className.endsWith('-fill-inverse')
            )
          )
          // Add the new fill class
          if (isMobile) {
            svg.classList.add(newFillClass)
          } else {
            svg.classList.add(inverseFillClass)
          }
        })
      })
    }
  }
}

if (!customElements.get('basis-animated-menu')) {
  customElements.define('basis-animated-menu', AnimatedMenu)
}
